import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../api/client';
import {
  formatFinancialPrice,
  formatMacd,
  formatRsi,
  formatPercentage,
  formatRatio,
} from '../../shared/domain/formatters';

import { CryptoTickerBar } from '../../components/common';
import {
  CANONICAL_SPOT_SYMBOLS,
  VENUE_METADATA,
  toShortSymbol,
} from '../../shared/constants/watchlist';
import { CRYPTO_BILINGUAL } from '../../shared/constants/bilingualTitles';

export const INDICATOR_SYMBOLS = CANONICAL_SPOT_SYMBOLS.map((sym) => ({
  symbol: sym,
  label: `${toShortSymbol(sym)}/USDT`,
  icon: VENUE_METADATA[sym].icon,
}));

export const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'];

import { IndicatorCellData } from './types';
export type { IndicatorCellData };


export interface IndicatorMatrixEnvelope {
  schema_version?: string;
  symbol?: string;
  generated_at?: string;
  bar_close_utc?: string;
  unified_mark_price?: string;
  timeframes?: string[];
  cells?: Record<string, IndicatorCellData>;
  resonance_summary?: {
    bullish_timeframes?: number;
    bearish_timeframes?: number;
    td9_active_setups?: number;
    resonance_tag?: string;
  };
  cached?: boolean;
  stale?: boolean;
  status?: string;
}

import { VisibilityRequestCoordinator } from '../regime/TrendWeatherGrid';

export const IndicatorsMatrixView: React.FC = () => {
  const [selectedSymbol, setSelectedSymbol] = useState<string>(() => {
    // Check url hash for symbol parameter
    try {
      const hash = window.location.hash;
      const match = hash.match(/symbol=([A-Za-z0-9]+)/);
      if (match && match[1]) {
        return match[1].toUpperCase();
      }
    } catch {
      // fallback
    }
    return 'BTCUSDT';
  });

  const [data, setData] = useState<IndicatorMatrixEnvelope | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
  const coordinatorRef = useRef<VisibilityRequestCoordinator<IndicatorMatrixEnvelope>>(new VisibilityRequestCoordinator());

  const fetchData = useCallback(async (sym: string, bypassCache = false) => {
    setLoading(true);
    try {
      coordinatorRef.current.cancelInFlight();
      const res = await coordinatorRef.current.execute(
        (signal) => apiClient.getIndicatorsMatrix(sym, bypassCache, { signal }) as Promise<IndicatorMatrixEnvelope>,
        true,
      );
      if (!res) return;
      if (res.cells && (!res.symbol || res.symbol === sym)) {
        setData(res);
      }
    } catch (err) {
      console.error('getIndicatorsMatrix error:', err);
    } finally {
      if (!coordinatorRef.current.hasActivePromise()) {
        setLoading(false);
      }
    }
  }, []);

  const handleSelectSymbol = (sym: string) => {
    setSelectedSymbol(sym);
    setData((prev) => (prev ? { ...prev, status: 'SYNCING' } : null));
    window.location.hash = `#/indicators?symbol=${sym}`;
    fetchData(sym);
  };

  // 1. Synchronize symbol when URL hash changes
  useEffect(() => {
    const handleHash = () => {
      const match = window.location.hash.match(/symbol=([A-Za-z0-9]+)/);
      if (match && match[1]) {
        const nextSym = match[1].toUpperCase();
        setSelectedSymbol(nextSym);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  // 2. Stage 066/070: 30-second cadence + Page Visibility pause/resume
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let isSubscribed = true;

    const startTimer = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        if (!document.hidden && isSubscribed) {
          fetchData(selectedSymbol);
        }
      }, 30000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        coordinatorRef.current.onHide();
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      } else {
        if (isSubscribed) {
          coordinatorRef.current.onResume();
          fetchData(selectedSymbol);
          startTimer();
        }
      }
    };

    fetchData(selectedSymbol);
    if (!document.hidden) {
      startTimer();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      isSubscribed = false;
      coordinatorRef.current.cancelInFlight();
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedSymbol, fetchData]);

  // Stage 066: 5-second cooldown timer for manual refresh
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const cdTimer = setInterval(() => {
      setCooldownRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(cdTimer);
  }, [cooldownRemaining]);

  const handleManualRefresh = useCallback(() => {
    if (cooldownRemaining > 0 || loading) return;
    fetchData(selectedSymbol, true);
    setCooldownRemaining(5);
  }, [cooldownRemaining, loading, selectedSymbol, fetchData]);

  const summary = data?.resonance_summary || {};
  const resTag = summary.resonance_tag || '中性蓄势';
  let resTagCls = 'sig-neutral';
  if (resTag.includes('多头')) {
    resTagCls = 'sig-bullish';
  } else if (resTag.includes('空头')) {
    resTagCls = 'sig-bearish';
  } else if (resTag.includes('TD9')) {
    resTagCls = 'sig-td9-countdown';
  }

  const markPrice = data?.unified_mark_price
    ? `${formatFinancialPrice(data.unified_mark_price, selectedSymbol)} USDT`
    : '-- USDT';

  const cells = data?.cells || {};

  return (
    <div className="view-panel active" id="view-indicators-matrix" role="tabpanel">
      {/* View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <span style={{ fontSize: '18px' }}>📊</span>
            <h2 className="bilingual-header-title-zh">{CRYPTO_BILINGUAL.indicators.zh}</h2>
          </div>
          <span className="bilingual-header-subtitle-en">{CRYPTO_BILINGUAL.indicators.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            7 标的 × 7 周期多时间尺度技术指标物化 · 纯 Decimal 零浮点 · 齿轮驱动闭合水位判定 · MACD / RSI / TD9 / BOLL / MA
          </p>
        </div>
      </div>

      {/* Top Controls & Subtabs Bar */}
      <div className="indicator-ctrl-bar">
        <CryptoTickerBar
          id="indicator-symbol-tabs"
          selectedSymbol={selectedSymbol}
          onSelectSymbol={(sym) => handleSelectSymbol(sym)}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span className={`kpi-badge ${data?.status === 'FRESH' ? 'badge-green' : data?.status === 'STALE' ? 'badge-amber' : 'badge-rose'}`} id="ind-cache-badge">
            物化缓存: {data?.status || (loading ? 'SYNCING' : 'EMPTY')}
          </span>
          <button
            className="filter-pill"
            id="btn-refresh-indicators"
            onClick={handleManualRefresh}
            disabled={loading || cooldownRemaining > 0}
            style={{
              background: 'var(--bg-surface-hover)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              cursor: loading || cooldownRemaining > 0 ? 'not-allowed' : 'pointer',
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '11px',
              opacity: loading || cooldownRemaining > 0 ? 0.6 : 1,
            }}
          >
            <span>{cooldownRemaining > 0 ? `🔄 冷却中 (${cooldownRemaining}s)` : '🔄 强制刷新'}</span>
          </button>
        </div>
      </div>

      {/* Symbol Resonance Summary Card */}
      <div className="indicator-resonance-card" id="ind-resonance-card">
        <div className="ind-stat-item">
          <span className="ind-stat-label">标的 / 标记价</span>
          <div className="ind-stat-val">
            <span id="ind-active-symbol-text">{selectedSymbol}</span>
            <span style={{ fontSize: '13px', color: 'var(--accent-cyan)' }} id="ind-mark-price">
              {markPrice}
            </span>
          </div>
        </div>

        <div className="ind-stat-item">
          <span className="ind-stat-label">多周期共振</span>
          <div className="ind-stat-val">
            <span className={`sig-badge ${resTagCls}`} id="ind-resonance-tag" style={{ fontSize: '12px', padding: '2px 8px' }}>
              {resTag}
            </span>
          </div>
        </div>

        <div className="ind-stat-item">
          <span className="ind-stat-label">多空能量比</span>
          <div className="ind-stat-val" style={{ fontSize: '13px' }}>
            <span style={{ color: '#34d399' }} id="ind-bull-count">
              多头 {summary.bullish_timeframes ?? 0}/7
            </span>
            <span style={{ color: 'var(--text-muted)' }}>/</span>
            <span style={{ color: '#fb7185' }} id="ind-bear-count">
              空头 {summary.bearish_timeframes ?? 0}/7
            </span>
          </div>
        </div>

        <div className="ind-stat-item">
          <span className="ind-stat-label">TD9 异动</span>
          <div className="ind-stat-val" style={{ fontSize: '13px' }}>
            <span id="ind-td9-count">{summary.td9_active_setups ?? 0} 末端序列(8/9)</span>
          </div>
        </div>

        <div className="ind-stat-item">
          <span className="ind-stat-label">调度水位</span>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            <span>1m 闭合 · 5m~1d 驱动 (W≥3s)</span>
          </div>
        </div>
      </div>

      {/* Indicator Grid Table */}
      <section className="panel-card" style={{ padding: '10px 14px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            borderBottom: '1px solid var(--border-subtle)',
            paddingBottom: '8px',
          }}
        >
          <div className="bilingual-card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px' }}>📈</span>
              <h3 className="bilingual-card-title-zh" style={{ margin: 0 }}>
                7 周期技术指标流矩阵
              </h3>
              <span className="venue-pill venue-hyperliquid" style={{ fontSize: '10px' }}>
                100% 纯 Decimal 算子
              </span>
            </div>
            <span className="bilingual-card-subtitle-en">TECHNICAL INDICATORS STREAM TABLE</span>
          </div>
          <div style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
            <span>更新时间: </span>
            <span id="ind-generated-at" style={{ color: 'var(--color-cyan)' }}>
              {data?.generated_at ? data.generated_at.replace('T', ' ').slice(11, 19) : '--:--:--'}
            </span>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="styled-table" id="indicator-matrix-table">
            <thead>
              <tr>
                <th style={{ width: '70px' }}>周期</th>
                <th style={{ width: '135px' }}>K线闭合时间</th>
                <th style={{ width: '100px' }}>收盘价</th>
                <th style={{ width: '160px' }}>MACD (12, 26, 9)</th>
                <th style={{ width: '140px' }}>RSI (14 & 6)</th>
                <th style={{ width: '140px' }}>DEMARK TD9 序列</th>
                <th style={{ width: '170px' }}>布林带 (20, 2)</th>
                <th style={{ width: '175px' }}>斐波那契 (S/R 水位)</th>
                <th style={{ width: '160px' }}>均线系统 (MA20/50)</th>
                <th style={{ width: '75px' }}>时效</th>
              </tr>
            </thead>
            <tbody id="indicator-matrix-tbody">
              {TIMEFRAMES.map((tf) => {
                const c = cells[tf] || { timeframe: tf };
                const closePrice = formatFinancialPrice(c.close_price, selectedSymbol);
                const barClose = c.bar_close_utc ? c.bar_close_utc.replace('T', ' ').replace('Z', '') : '--';

                // MACD badge
                let macdSig = <span className="sig-badge sig-neutral">中性</span>;
                if (c.macd_signal === 'GOLDEN_CROSS') {
                  macdSig = <span className="sig-badge sig-bullish">金叉 ↗</span>;
                } else if (c.macd_signal === 'DEAD_CROSS') {
                  macdSig = <span className="sig-badge sig-bearish">死叉 ↘</span>;
                } else if (c.macd_signal === 'BULLISH') {
                  macdSig = <span className="sig-badge sig-bullish">多头</span>;
                } else if (c.macd_signal === 'BEARISH') {
                  macdSig = <span className="sig-badge sig-bearish">空头</span>;
                }

                const macdDif = formatMacd(c.macd_dif, selectedSymbol);
                const macdDea = formatMacd(c.macd_dea, selectedSymbol);

                // RSI badge
                let rsiSig = <span className="sig-badge sig-neutral">正常</span>;
                if (c.rsi_signal === 'OVERSOLD') {
                  rsiSig = <span className="sig-badge sig-rsi-oversold">超卖 ≤22</span>;
                } else if (c.rsi_signal === 'OVERBOUGHT') {
                  rsiSig = <span className="sig-badge sig-rsi-overbought">超买 ≥78</span>;
                }
                const rsi14 = formatRsi(c.rsi_14);
                const rsi6 = formatRsi(c.rsi_6);

                // TD9 badge
                let td9Sig = <span className="sig-badge sig-neutral">无序列</span>;
                const count = c.td9_count || 0;
                const dir = c.td9_direction || 'NEUTRAL';
                if (count === 9 && dir === 'BUY') {
                  td9Sig = <span className="sig-badge sig-td9-buy">买点 9 {c.td9_is_perfect ? '★' : ''}</span>;
                } else if (count === 9 && dir === 'SELL') {
                  td9Sig = <span className="sig-badge sig-td9-sell">卖点 9 {c.td9_is_perfect ? '★' : ''}</span>;
                } else if (count >= 8) {
                  td9Sig = <span className="sig-badge sig-td9-countdown">{dir === 'BUY' ? '买' : '卖'} {count}</span>;
                } else if (count > 0) {
                  td9Sig = <span className="sig-badge sig-neutral">{dir === 'BUY' ? '买' : '卖'} {count}</span>;
                }

                // Bollinger badge
                let bollSig = <span className="sig-badge sig-neutral">常规</span>;
                if (c.boll_signal === 'TOUCH_UPPER') {
                  bollSig = <span className="sig-badge sig-bearish">触上轨</span>;
                } else if (c.boll_signal === 'TOUCH_LOWER') {
                  bollSig = <span className="sig-badge sig-bullish">触下轨</span>;
                } else if (c.boll_signal === 'SQUEEZE') {
                  bollSig = <span className="sig-badge sig-td9-countdown">极限收口</span>;
                } else if (c.boll_signal === 'EXPANSION') {
                  bollSig = <span className="sig-badge sig-rsi-oversold">张口发散</span>;
                }
                const bollBw = formatPercentage(c.boll_bandwidth_pct);
                const bollPb = formatRatio(c.boll_percent_b);

                // MA badge
                let maSig = <span className="sig-badge sig-neutral">缠绕</span>;
                if (c.ma_trend === 'BULLISH_ALIGNMENT') {
                  maSig = <span className="sig-badge sig-bullish">多头排列</span>;
                } else if (c.ma_trend === 'BEARISH_ALIGNMENT') {
                  maSig = <span className="sig-badge sig-bearish">空头排列</span>;
                }
                const ma20Dist = formatPercentage(c.ma20_dist_pct, { signed: true });

                // Fibonacci S/R presentation
                const fibResPrice = c.fib_resistance_price
                  ? formatFinancialPrice(c.fib_resistance_price, selectedSymbol)
                  : null;
                const fibSupPrice = c.fib_support_price
                  ? formatFinancialPrice(c.fib_support_price, selectedSymbol)
                  : null;
                const fibResLvl = c.fib_resistance_level;
                const fibSupLvl = c.fib_support_level;
                const fibResDist = c.fib_resistance_dist_pct;
                const fibSupDist = c.fib_support_dist_pct;

                const fibTooltip = c.fib_levels
                  ? [
                      `斐波那契全阶梯水位 (${c.fib_zone || 'ZONE'}):`,
                      `  0.0% (高点): ${formatFinancialPrice(c.fib_levels['0.0'], selectedSymbol)}`,
                      ` 23.6%: ${formatFinancialPrice(c.fib_levels['23.6'], selectedSymbol)}`,
                      ` 38.2%: ${formatFinancialPrice(c.fib_levels['38.2'], selectedSymbol)}`,
                      ` 50.0%: ${formatFinancialPrice(c.fib_levels['50.0'], selectedSymbol)}`,
                      ` 61.8%: ${formatFinancialPrice(c.fib_levels['61.8'], selectedSymbol)}`,
                      ` 78.6%: ${formatFinancialPrice(c.fib_levels['78.6'], selectedSymbol)}`,
                      `100.0% (低点): ${formatFinancialPrice(c.fib_levels['100.0'], selectedSymbol)}`,
                    ].join('\n')
                  : undefined;

                const statusBadge =
                  c.status === 'computed' ? (
                    <span className="strip-pill-badge badge-green">COMPUTED</span>
                  ) : (
                    <span className="strip-pill-badge badge-amber">NO_DATA</span>
                  );

                return (
                  <tr key={tf}>
                    <td>
                      <span className="timeframe-pill">{tf}</span>
                    </td>
                    <td>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }}>{barClose}</span>
                    </td>
                    <td>
                      <strong style={{ fontFamily: 'var(--font-mono)' }}>{closePrice}</strong>
                    </td>
                    <td>
                      {macdSig}
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                        DIF: {macdDif} / DEA: {macdDea}
                      </div>
                    </td>
                    <td>
                      {rsiSig}
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                        RSI14: {rsi14} / 6: {rsi6}
                      </div>
                    </td>
                    <td>{td9Sig}</td>
                    <td>
                      {bollSig}
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                        带宽: {bollBw} / %B: {bollPb}
                      </div>
                    </td>
                    <td>
                      {fibResPrice || fibSupPrice ? (
                        <div
                          className="fib-sr-cell"
                          title={fibTooltip}
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '11px',
                            cursor: fibTooltip ? 'help' : 'default',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              color: 'var(--color-bearish)',
                              marginBottom: '2px',
                            }}
                          >
                            <span>
                              <span style={{ fontWeight: 600, marginRight: '3px' }}>压</span>
                              <span>{fibResPrice ?? 'ATH'}</span>
                              {fibResLvl && (
                                <span style={{ color: 'var(--text-muted)', marginLeft: '2px', fontSize: '10px' }}>
                                  ({fibResLvl})
                                </span>
                              )}
                            </span>
                            {fibResDist && <span style={{ fontSize: '10px', marginLeft: '4px' }}>{fibResDist}</span>}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              color: 'var(--color-bullish)',
                            }}
                          >
                            <span>
                              <span style={{ fontWeight: 600, marginRight: '3px' }}>支</span>
                              <span>{fibSupPrice ?? 'ATL'}</span>
                              {fibSupLvl && (
                                <span style={{ color: 'var(--text-muted)', marginLeft: '2px', fontSize: '10px' }}>
                                  ({fibSupLvl})
                                </span>
                              )}
                            </span>
                            {fibSupDist && <span style={{ fontSize: '10px', marginLeft: '4px' }}>{fibSupDist}</span>}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '11px', fontFamily: 'var(--font-mono)' }}>
                          --
                        </span>
                      )}
                    </td>
                    <td>
                      {maSig}
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                        距MA20: {ma20Dist}
                      </div>
                    </td>
                    <td>{statusBadge}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
